import React, { useEffect, useState } from 'react';
import { BrowserRouter as Router, Routes, Route, useLocation } from 'react-router-dom';
import { HelmetProvider } from 'react-helmet-async';
import RegisterForm from './pages/RegisterForm';
import Dashboard from './components/Dashboard';
import Navbar from './components/Navbar';
import LoginPage from './pages/LoginPage';
import Home from './pages/Home';
import About from './pages/About';
import Documentation from './pages/Documentation';
import PrivacyPolicy from './pages/PrivacyPolicy';
import DeleteAccount from './pages/DeleteAccount';
import TermsOfService from './pages/TermsOfService';
import MyEventsPage from './pages/MyEventsPage';
import AllEventsPage from './pages/AllEventsPage';
import EventDetails from './pages/EventDetails';
import EditEvent from './pages/EditEvent';
import EditProfile from "./pages/EditProfile";
import Bids from "./pages/Bids";
import EventBids from "./pages/EventBids";
import MyGigs from "./pages/MyGigs";
import Chat from "./pages/Chat";

import { AuthProvider } from './context/AuthContext';
import { SuccessFailureProvider } from './context/SuccessFailureContext';
import { EventsProvider } from './context/EventsContext';
import { ThemeProvider } from './context/ThemeContext';
import MuiThemeBridge from './context/MuiThemeBridge';
import ErrorBoundary from './components/ErrorBoundary';
import './App.css';
import PrivateRoute from "./components/PrivateRoute";
import PublicProfile from "./pages/PublicProfile";
import SuccessModal from './components/SuccessModal';
import FailureModal from './components/FailureModal';
import NotificationsPage from "./pages/NotificationsPage";
import TestFormBuilder from "./pages/TestFormBuilder";
import FormBuilderPage from "./pages/FormBuilderPage";
import AdminDashboard from "./pages/AdminDashboard";
import FormResponsePage from "./pages/FormResponsePage";
import MyTicketPage from "./pages/MyTicketPage";
import CheckInScanPage from "./pages/CheckInScanPage";
import CheckInWallPage from "./pages/CheckInWallPage";
import LiveDashboardPage from "./pages/LiveDashboardPage";
import WalletPage from "./pages/WalletPage";
import AdminWithdrawals from "./pages/AdminWithdrawals";
import EmbedTicket from "./pages/EmbedTicket";

import SettingsPage from "./pages/SettingsPage";
import CommunitiesPage from "./pages/CommunitiesPage";
import WorkspacePage from "./pages/WorkspacePage";
import RoomPage from "./pages/RoomPage";

// Hide global chrome (navbar) on full-screen/embeddable routes.
const AppChrome = ({ pageTitle, setPageTitle }) => {
  const location = useLocation();
  const bare = location.pathname.startsWith('/embed') || location.pathname.endsWith('/wall') || location.pathname.endsWith('/live');
  if (bare) return null;
  return <Navbar pageTitle={pageTitle} setPageTitle={setPageTitle} />;
};

function App() {
  const [pageTitle, setPageTitle] = useState('EventsAwa');

  useEffect(() => {
    document.title = pageTitle;
  }, [pageTitle]);

  return (
    <HelmetProvider>
      <Router>
        <ThemeProvider>
        <MuiThemeBridge>
        <AuthProvider>
          <SuccessFailureProvider>
            <EventsProvider>
              <ErrorBoundary>
                <AppChrome pageTitle={pageTitle} setPageTitle={setPageTitle} />
                <Routes>
                  <Route path="/" element={<Home />} />
                  <Route path="/register" element={<RegisterForm />} />
                  <Route path="/login" element={<LoginPage />} />
                  <Route path="/dashboard" element={<Dashboard />} />
                  <Route path="/my-events" element={<MyEventsPage />} />
                  <Route path="/all-events" element={<AllEventsPage />} />
                  <Route path="/documentation" element={<Documentation />} />
                  <Route path="/privacy-policy" element={<PrivacyPolicy />} />
                  <Route path="/delete-account" element={<DeleteAccount />} />
                  <Route path="/terms-of-service" element={<TermsOfService />} />
                  <Route path="/events/:event_slug" element={<EventDetails />} />
                  <Route path="/events/:event_slug/update" element={<EditEvent />} />
                  <Route path="/profile/:slug" element={<PublicProfile />} />
                  <Route element={<PrivateRoute />}>
                    <Route path="/profile/edit" element={<EditProfile />} />
                  </Route>
                  <Route path="/about" element={<About />} />
                  <Route path="/bids" element={<Bids />} />
                  <Route path="/bids/:eventId" element={<EventBids />} />
                  <Route path="/my-gigs" element={<MyGigs />} />
                  <Route path="/chat/:eventId/:recipientId" element={<Chat />} />
                  <Route path="/notifications" element={
                    // <PrivateRoute>
                    <NotificationsPage />
                    // </PrivateRoute>
                  } />
                  <Route path="/form-builder/:eventId" element={
                    <PrivateRoute>
                      <FormBuilderPage />
                    </PrivateRoute>
                  } />
                  {/* Keep the old route for backward compatibility */}
                  <Route path="/test-form-builder/:eventId?" element={
                    <PrivateRoute>
                      <TestFormBuilder />
                    </PrivateRoute>
                  } />
                  <Route path="/admin" element={<AdminDashboard />} />
                  <Route path="/forms/response/:responseId" element={<FormResponsePage />} />
                  <Route path="/events/:eventId/ticket" element={
                    <PrivateRoute>
                      <MyTicketPage />
                    </PrivateRoute>
                  } />
                  <Route path="/events/:eventId/scan" element={
                    <PrivateRoute>
                      <CheckInScanPage />
                    </PrivateRoute>
                  } />
                  <Route path="/events/:eventId/wall" element={
                    <PrivateRoute>
                      <CheckInWallPage />
                    </PrivateRoute>
                  } />
                  <Route path="/events/:eventId/live" element={
                    <PrivateRoute>
                      <LiveDashboardPage />
                    </PrivateRoute>
                  } />
                  <Route path="/embed/event/:slug" element={<EmbedTicket />} />
                  <Route path="/wallet" element={<PrivateRoute><WalletPage /></PrivateRoute>} />
                  <Route path="/admin/withdrawals" element={<PrivateRoute><AdminWithdrawals /></PrivateRoute>} />
                  <Route path="/communities" element={<PrivateRoute><CommunitiesPage /></PrivateRoute>} />
                  <Route path="/communities/:id" element={<PrivateRoute><WorkspacePage /></PrivateRoute>} />
                  <Route path="/communities/rooms/:roomId" element={<PrivateRoute><RoomPage /></PrivateRoute>} />
                  <Route path="/settings" element={
                    <PrivateRoute>
                      <SettingsPage />
                    </PrivateRoute>
                  } />
                </Routes>
                <SuccessModal />
                <FailureModal />
              </ErrorBoundary>
            </EventsProvider>
          </SuccessFailureProvider>
        </AuthProvider>
        </MuiThemeBridge>
        </ThemeProvider>
      </Router>
    </HelmetProvider>
  );
};

export default App;
